import { Stack, Typography } from '@mui/joy';
import { CaretLeft, CaretRight, Info } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { IContactGroupAddress } from 'contacts/types';
import { Button } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Email, LabeledValue, NeutralChip, PhoneNumber } from 'core/elements';
import { Color, Direction, Level, Variant } from 'theme/enums';

const LABEL_OPTIONS = {
  labelLevel: Level.BODY_SM,
  labelSx: { fontWeight: 400, whiteSpace: 'nowrap' },
  inline: true,
};

interface IProps {
  vendor: IContactGroupAddress;
  isSelected?: boolean;
  onVendorInfo: (vendor: IContactGroupAddress) => void;
}

export const AlternateVendorCard = ({ vendor, isSelected, onVendorInfo }: IProps) => {
  const { t } = useTranslation();

  const name = vendor?.name || DEFAULT_PLACEHOLDER;
  const accountsPayableId = vendor?.accountsPayableId;
  const primaryContact = vendor?.primaryContact;

  return (
    <Stack
      direction={Direction.ROW}
      onClick={() => onVendorInfo(vendor)}
      sx={{
        alignItems: 'flex-start',
        gap: 1.25,
        padding: '12px 16px',
        backgroundColor: isSelected ? 'neutral.100' : 'neutral.50',
        border: '1px solid',
        borderColor: isSelected ? 'primary.400' : 'neutral.200',
        borderRadius: '6px',
        width: '100%',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          borderColor: isSelected ? 'primary.400' : 'primary.200',
          backgroundColor: isSelected ? 'neutral.50' : 'neutral.100',
        },
      }}
    >
      <Stack sx={{ flex: 1, gap: 0.5 }}>
        <Stack
          direction={Direction.ROW}
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}
        >
          <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
            <Typography level={Level.TITLE_MD} color={Color.PRIMARY}>
              {name}
            </Typography>
            {accountsPayableId && <NeutralChip>{accountsPayableId}</NeutralChip>}
          </Stack>

          <Button
            variant={Variant.SOFT}
            color={Color.PRIMARY}
            text={isSelected ? t('buttonLabelHide') : t('buttonLabelMoreInfo')}
            startDecorator={<Info />}
            endDecorator={isSelected ? <CaretLeft /> : <CaretRight />}
            onClick={(e) => {
              e.stopPropagation();
              onVendorInfo(vendor);
            }}
            sx={{ minWidth: 130, justifyContent: 'space-between' }}
          />
        </Stack>

        <LabeledValue
          label={t('labelContactName')}
          value={primaryContact?.contactName || DEFAULT_PLACEHOLDER}
          options={LABEL_OPTIONS}
        />

        <LabeledValue
          label={t('labelContactEmail')}
          value={<Email email={primaryContact?.email} />}
          options={LABEL_OPTIONS}
        />

        <LabeledValue
          label={t('labelContactPhone')}
          value={<PhoneNumber phone={primaryContact?.phonenumber} />}
          options={LABEL_OPTIONS}
        />
      </Stack>
    </Stack>
  );
};
